import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');

const PurchaseCreditNotes: React.FC = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/purchase-credit-notes');
      setNotes(r.data?.creditNotes || []);
    } catch {
      toast.error('No se pudieron cargar las notas de crédito');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchNotes(); }, []);

  const cancelNote = async (nc: any) => {
    const ok = await confirmDialog({
      title: 'Anular nota de crédito',
      description: `¿Anular ${nc.number}? ${nc.affectsStock ? 'Se repone el stock devuelto y ' : ''}se revierte el crédito en la cuenta corriente del proveedor.`,
      confirmText: 'Anular',
    });
    if (!ok) return;
    try {
      await axios.post(`/api/purchase-credit-notes/${nc.id}/cancel`);
      toast.success('Nota de crédito anulada');
      fetchNotes();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'No se pudo anular');
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Notas de crédito de compra</h2>
          <p className="text-sm text-muted-foreground">Devoluciones, ajustes y reintegros de proveedores. Bajan la deuda en la cuenta corriente.</p>
        </div>
        <Button onClick={() => navigate('/purchases/credit-notes/new')} className="gap-1.5"><PlusIcon className="h-4 w-4" /> Nueva nota de crédito</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Cargando...</div>
          ) : notes.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Sin notas de crédito registradas.</div>
          ) : (
            <div className="cq-table">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b">
                    <TableHead className="h-10 px-3 text-xs">N°</TableHead>
                    <TableHead className="h-10 px-3 text-xs">Fecha</TableHead>
                    <TableHead className="h-10 px-3 text-xs">Proveedor</TableHead>
                    <TableHead className="h-10 px-3 text-xs cq-hide-narrow">OC</TableHead>
                    <TableHead className="h-10 px-3 text-xs cq-hide-narrow">N° prov.</TableHead>
                    <TableHead className="h-10 px-3 text-xs cq-hide-narrow">Tipo</TableHead>
                    <TableHead className="h-10 px-3 text-xs text-right">Monto</TableHead>
                    <TableHead className="h-10 px-3 text-xs">Estado</TableHead>
                    <TableHead className="h-10 w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notes.map(nc => (
                    <TableRow key={nc.id} className="border-b hover:bg-muted/30">
                      <TableCell className="px-3 py-2 font-mono text-sm">{nc.number}</TableCell>
                      <TableCell className="px-3 py-2 text-sm whitespace-nowrap">{fmtDate(nc.date || nc.createdAt)}</TableCell>
                      <TableCell className="px-3 py-2 text-sm">{nc.providerFantasyName || nc.providerName || '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{nc.purchaseNumber || '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{nc.providerNcNumber || '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-xs cq-hide-narrow">
                        <Badge variant="secondary" className="text-xs">{nc.affectsStock ? 'Devolución' : 'Ajuste'}</Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2 text-sm text-right font-semibold whitespace-nowrap">{fmtARS(nc.total)}</TableCell>
                      <TableCell className="px-3 py-2">
                        <Badge variant={nc.status === 'cancelled' ? 'outline' : 'secondary'}
                          className={`text-xs ${nc.status === 'cancelled' ? 'text-muted-foreground line-through' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'}`}>
                          {nc.status === 'cancelled' ? 'Anulada' : 'Activa'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-2 text-right">
                        {nc.status !== 'cancelled' && (
                          <Tip label="Anular"><button type="button" onClick={() => cancelNote(nc)} className="rounded p-1 text-muted-foreground hover:text-red-500"><XMarkIcon className="h-4 w-4" /></button></Tip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseCreditNotes;
